import type { MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import type { MembershipBannerProps } from '@noths/polaris-client-ribbons-design-system';

import type { ReduxApplicationState } from 'src/redux/types';
import { trackCTAClick } from 'src/tracking/trackCTAClick';
import { trackImpression } from 'src/tracking/trackImpression';

/**
 * View model for the membership banner. Owns the content read, the mapping onto
 * the banner's props and the impression/click tracking, so the presentational
 * banner stays pure props.
 *
 * Returns null when there is no banner content, which is how the section is
 * omitted from the page.
 */
export type MembershipBannerViewModel = Required<
  Pick<MembershipBannerProps, 'heading' | 'onButtonClick' | 'onVisible'>
> &
  Pick<MembershipBannerProps, 'backgroundImageUrl' | 'buttonHref' | 'buttonText' | 'copy'>;

export const useMembershipBannerViewModel = (): MembershipBannerViewModel | null => {
  const content = useSelector((state: ReduxApplicationState) => state.content.membershipBanner);

  if (!content) {
    return null;
  }

  const trackingArgs = {
    category: 'Membership banner',
    contentType: `membership banner|${content.trackingTitle}`,
    label: content.trackingTitle,
  };

  return {
    backgroundImageUrl: content.backgroundImageURL,
    buttonHref: content.link.href,
    buttonText: content.link.text,
    copy: content.copy,
    heading: content.heading,
    onButtonClick: (e: MouseEvent<HTMLAnchorElement>) => trackCTAClick(e, trackingArgs),
    onVisible: () => trackImpression(trackingArgs),
  };
};
